/**
 * @file: SortOwnerBar.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The sort + owner control row. Sort drives the list `sorter`; owner drives
 * `filter.assigneeId` (its options come from the store's service-agent roster).
 */

import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { kLeadSortOptions } from '@euler/app/flows/leads/pages/list/functions/leadListConstants';
import { useOwnerFilter } from '@euler/app/flows/leads/pages/list/functions/useOwnerFilter';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { ArrowUpDown, User } from 'lucide-react-native';
import { memo, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { ControlPill } from './ControlPill';

export const SortOwnerBar = memo(
  ({
    sortKey,
    onSelectSort,
    assignee,
    onSelectAssignee,
  }: {
    sortKey: string;
    onSelectSort: (key: string) => void;
    assignee: { id: number; name: string } | undefined;
    onSelectAssignee: (next: { id: number; name: string } | undefined) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { showActionSheetWithOptions } = useActionSheet();
    // Lazy: the roster loads on the owner pill's first tap, never on mount, so
    // opening the filter panel doesn't suspend (and flash-reload) the list.
    const openOwner = useOwnerFilter(assignee, onSelectAssignee);

    const sortLabel = useMemo(
      () =>
        (kLeadSortOptions.find(o => o.key === sortKey) ?? kLeadSortOptions[0])
          .label,
      [sortKey],
    );

    const openSortSheet = useCallback(() => {
      const options = [...kLeadSortOptions.map(o => o.label), '取消'];
      showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: kLeadSortOptions.length,
          title: '排序方式',
        },
        selected => {
          if (selected == null || selected >= kLeadSortOptions.length) return;
          onSelectSort(kLeadSortOptions[selected].key);
        },
      );
    }, [onSelectSort, showActionSheetWithOptions]);

    return (
      <View
        style={{
          flexDirection: 'row',
          gap: rpx(10),
          marginHorizontal: rpx(16),
          marginBottom: rpx(14),
        }}
      >
        <ControlPill
          icon={<ArrowUpDown size={rpx(16)} color={LD.steel} />}
          label={sortLabel}
          onPress={openSortSheet}
        />
        <ControlPill
          icon={<User size={rpx(16)} color={LD.steel} />}
          label={`负责人：${assignee?.name ?? '全部'}`}
          onPress={() => void openOwner()}
        />
      </View>
    );
  },
);
